/**
 * TeachingSubTabs Component
 * Sub-tab navigation within the Teaching tab: Active, Upcoming, Completed, Draft, Archived.
 * Matches the Figma "Elements Subtab / My Profile / Teaching" pattern.
 * Calls useUserRitualTeachings directly with subTab filter for data isolation.
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { TeachingCard } from "@/components/cards/TeachingCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { useUserRitualTeachings } from "@/features/profile/hooks/useUserRitualTeachings"
import {
  useArchiveRitual,
  useUnarchiveRitual,
} from "@/features/rituals/hooks/useGlobalArchiveRitual"
import { formatRitualType } from "@/features/rituals/types"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { TeachingSubTab } from "../types/my-rituality.types"
import { TabEmptyState } from "./TabEmptyState"
import { TeachingsCompletedCard } from "./TeachingsCompletedCard"

interface TeachingSubTabsProps {
  userId: string
  searchQuery?: string
}

export function TeachingSubTabs({ userId, searchQuery }: TeachingSubTabsProps) {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const [activeSubTab, setActiveSubTab] = useState<TeachingSubTab>("active")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)
  const toggleSave = useToggleRitualSave()

  const { data, isLoading, isError } = useUserRitualTeachings(userId, {
    limit: 50,
    search: debouncedSearch || undefined,
    subTab: activeSubTab,
  })
  const archiveRitual = useArchiveRitual()
  const unarchiveRitual = useUnarchiveRitual()

  const subTabs: { key: TeachingSubTab; label: string }[] = [
    { key: "active", label: t("teachingSubTabs.active") },
    { key: "upcoming", label: t("teachingSubTabs.upcoming") },
    { key: "completed", label: t("teachingSubTabs.completed") },
    { key: "draft", label: t("teachingSubTabs.draft") },
    { key: "archived", label: t("teachingSubTabs.archived") },
  ]

  const rituals =
    data?.rituals.map(ritual => {
      const coverImage = ritual.uploads?.find(u => u.isCover)?.filePath || undefined
      return {
        id: ritual.id,
        title: ritual.title,
        type: ritual.type,
        participationFee: ritual.participationFee,
        duration: ritual.duration,
        coverImage,
        image: coverImage,
        price: ritual.price,
        meetingLink: ritual.meetingLink,
        viewCount: ritual.viewCount,
        joinedCount: ritual.joinedCount,
        savedCount: ritual.savedCount,
        earnings: ritual.earnings,
        minimumParticipants: ritual.minimumParticipants,
        createdBy: ritual.createdBy,
        username: ritual.createdBy?.name || "Unknown",
        userHandle: ritual.createdBy?.username ? `@${ritual.createdBy.username}` : "",
        userAvatar: ritual.createdBy?.coverImage || undefined,
        userId: ritual.createdBy?.id,
        tags: [ritual.participationFee, formatRitualType(ritual.type)],
        isBookmarked: ritual.isSaved,
        onBookmarkClick: () => toggleSave.mutate(ritual.id),
        onEditClick: () => navigate(`/rituals/edit/${ritual.id}`),
        isArchived: ritual.isArchived,
        onArchiveClick: () => archiveRitual.mutate(ritual.id),
        onUnarchiveClick: () => unarchiveRitual.mutate(ritual.id),
        stats: {
          views: ritual.viewCount ?? 0,
          participants: ritual.joinedCount ?? 0,
          completed: 0,
          earning: ritual.earnings ?? 0,
        },
      }
    }) || []

  return (
    <div className="px-5 md:px-0">
      <div className="flex items-center justify-between">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-1.25 md:gap-3.75">
          <div className="flex justify-between items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              {t("tabs.teaching")}
            </div>
            <div className="md:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("tabs.search")}
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label={t("tabs.closeSearch")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label={t("tabs.search")}
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>

          <div className="w-px h-7.5 bg-text-primary/10 hidden md:block" />

          <div className="flex items-center h-7.5 gap-3.75 md:gap-5 text-sm text-white overflow-x-auto scrollbar-hide">
            {subTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveSubTab(tab.key)}
                className={cn(
                  "text-sm cursor-pointer transition-opacity whitespace-nowrap shrink-0",
                  activeSubTab === tab.key ? "font-semibold opacity-100" : "font-normal opacity-70",
                )}
              >
                {tab.label} ({data?.counts?.[tab.key] ?? 0})
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/rituals/create")}
          className="hidden md:block h-[50px] w-[220px] rounded-full border border-text-primary text-text-primary text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
        >
          {t("teachingSubTabs.addRitual")}
        </button>
      </div>

      {/* Description */}
      <p className="text-sm text-primary mt-3 mb-5 md:mb-2">{t("teachingSubTabs.description")}</p>

      <button
        type="button"
        onClick={() => navigate("/rituals/create")}
        className="block md:hidden h-[50px] w-full rounded-full border border-text-primary text-text-primary text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
      >
        {t("teachingSubTabs.addRitual")}
      </button>

      {/* Content */}
      {(() => {
        const totalCount = Object.values(data?.counts ?? {}).reduce((sum, c) => sum + c, 0)

        if (isLoading) {
          return (
            <div className="flex justify-center py-12">
              <p className="text-sm text-primary/50">{t("teachingSubTabs.loading")}</p>
            </div>
          )
        }

        if (isError) {
          return (
            <div className="flex justify-center py-12">
              <p className="text-sm text-red-400">{t("teachingSubTabs.error")}</p>
            </div>
          )
        }

        if (rituals.length === 0 && debouncedSearch) {
          return (
            <div className="pt-8">
              <p className="text-sm text-primary/50">{t("teachingSubTabs.empty")}</p>
            </div>
          )
        }

        if (rituals.length === 0 && totalCount === 0) {
          return (
            <TabEmptyState
              title={t("teachingSubTabs.emptyTitle")}
              description={t("teachingSubTabs.emptyDescription")}
              ctaLabel={t("teachingSubTabs.createRitual")}
              ctaTo="/rituals/create"
            />
          )
        }

        if (rituals.length === 0) {
          return (
            <div className="flex justify-center py-12">
              <p className="text-sm text-primary/50">{t("teachingSubTabs.empty")}</p>
            </div>
          )
        }

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7.5 md:mt-4">
            {rituals.map(ritual =>
              activeSubTab === "completed" ? (
                <div key={ritual.id} className="h-[401px]">
                  <TeachingsCompletedCard
                    id={ritual.id}
                    title={ritual.title}
                    type={ritual.type}
                    participationFee={ritual.participationFee}
                    duration={ritual.duration}
                    image={ritual.image}
                    price={ritual.price}
                    meetingLink={ritual.meetingLink}
                    viewCount={ritual.viewCount}
                    joinedCount={ritual.joinedCount}
                    savedCount={ritual.savedCount}
                    earnings={ritual.earnings}
                    minimumParticipants={ritual.minimumParticipants}
                  />
                </div>
              ) : (
                <TeachingCard
                  key={ritual.id}
                  id={ritual.id}
                  coverImage={ritual.image}
                  title={ritual.title}
                  type={ritual.type}
                  duration={ritual.duration}
                  participationFee={ritual.participationFee}
                  price={ritual.price}
                  tags={ritual.tags}
                  createdBy={ritual.createdBy}
                  isBookmarked={ritual.isBookmarked}
                  onBookmarkClick={ritual.onBookmarkClick}
                  onEditClick={ritual.onEditClick}
                  isArchived={ritual.isArchived}
                  onArchiveClick={ritual.onArchiveClick}
                  onUnarchiveClick={ritual.onUnarchiveClick}
                  stats={ritual.stats}
                />
              ),
            )}
          </div>
        )
      })()}
    </div>
  )
}
